/**
 * Base Chart Component - Foundation for all chart types with Canvas rendering
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';

export interface ChartTheme {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  background: string;
  foreground: string;
  muted: string;
  border: string;
  gridLines: string;
  textPrimary: string;
  textSecondary: string;
}

export interface ChartConfig {
  width?: number;
  height?: number;
  responsive?: boolean;
  animate?: boolean;
  theme?: Partial<ChartTheme>;
  margins?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  interactions?: boolean;
}

export interface ChartDataPoint {
  x: number | string;
  y: number;
  label?: string;
  color?: string;
  metadata?: Record<string, any>;
}

export interface TooltipData {
  x: number;
  y: number;
  content: React.ReactNode;
  visible: boolean;
}

interface BaseChartProps extends ChartConfig {
  data: ChartDataPoint[];
  className?: string;
  onDataPointClick?: (dataPoint: ChartDataPoint, index: number) => void;
  onDataPointHover?: (dataPoint: ChartDataPoint | null, index: number) => void;
  loading?: boolean;
  error?: string;
  customRender?: (
    ctx: CanvasRenderingContext2D,
    chartArea: { x: number; y: number; width: number; height: number },
    dataRanges: { xMin: number; xMax: number; yMin: number; yMax: number },
    dataToCanvas: (dataX: number, dataY: number) => { x: number; y: number }
  ) => void;
}

const defaultTheme: ChartTheme = {
  primary: '#3b82f6',
  secondary: '#64748b',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#06b6d4',
  background: '#ffffff',
  foreground: '#0f172a',
  muted: '#f8fafc',
  border: '#e2e8f0',
  gridLines: '#f1f5f9',
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
};

const defaultConfig: Required<ChartConfig> = {
  width: 400,
  height: 300,
  responsive: true,
  animate: true,
  theme: defaultTheme,
  margins: { top: 20, right: 20, bottom: 40, left: 60 },
  showGrid: true,
  showLegend: false,
  showTooltip: true,
  interactions: true,
};

export const BaseChart: React.FC<BaseChartProps> = ({
  data,
  className = '',
  onDataPointClick,
  onDataPointHover,
  loading = false,
  error,
  customRender,
  ...configProps
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  
  const [dimensions, setDimensions] = useState({ width: 400, height: 300 });
  const [tooltip, setTooltip] = useState<TooltipData>({
    x: 0,
    y: 0,
    content: null,
    visible: false,
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Merge config with defaults
  const config: Required<ChartConfig> = {
    ...defaultConfig,
    ...configProps,
    theme: { ...defaultTheme, ...configProps.theme },
    margins: { ...defaultConfig.margins, ...configProps.margins },
  };

  // Handle responsive sizing
  const updateDimensions = useCallback(() => {
    if (!config.responsive || !containerRef.current) return;

    const { offsetWidth } = containerRef.current;
    const aspectRatio = config.height / config.width;
    const newWidth = Math.max(300, offsetWidth);
    const newHeight = newWidth * aspectRatio;

    setDimensions({ width: newWidth, height: newHeight });
  }, [config.responsive, config.width, config.height]);

  useEffect(() => {
    if (config.responsive) {
      updateDimensions();
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    } else {
      setDimensions({ width: config.width, height: config.height });
    }
  }, [config.responsive, config.width, config.height, updateDimensions]);

  // Get device pixel ratio for crisp rendering
  const getPixelRatio = () => {
    return window.devicePixelRatio || 1;
  };

  // Setup canvas context with proper scaling
  const setupCanvas = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
    const pixelRatio = getPixelRatio();
    const { width, height } = dimensions;

    // Set display size
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Set actual size in memory
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;

    // Scale context to match device pixel ratio
    ctx.scale(pixelRatio, pixelRatio);
    
    return { width, height };
  };

  // Calculate chart area (excluding margins)
  const getChartArea = () => {
    const { width, height } = dimensions;
    const { margins } = config;
    
    return {
      x: margins.left,
      y: margins.top,
      width: width - margins.left - margins.right,
      height: height - margins.top - margins.bottom,
    };
  };

  // Draw grid lines
  const drawGrid = (ctx: CanvasRenderingContext2D, chartArea: ReturnType<typeof getChartArea>) => {
    if (!config.showGrid) return;

    ctx.save();
    ctx.strokeStyle = config.theme.gridLines;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);

    const gridLines = 5;
    
    // Vertical grid lines
    for (let i = 0; i <= gridLines; i++) {
      const x = chartArea.x + (chartArea.width / gridLines) * i;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.y);
      ctx.lineTo(x, chartArea.y + chartArea.height);
      ctx.stroke();
    }

    // Horizontal grid lines
    for (let i = 0; i <= gridLines; i++) {
      const y = chartArea.y + (chartArea.height / gridLines) * i;
      ctx.beginPath();
      ctx.moveTo(chartArea.x, y);
      ctx.lineTo(chartArea.x + chartArea.width, y);
      ctx.stroke();
    }

    ctx.restore();
  };

  // Draw chart border
  const drawBorder = (ctx: CanvasRenderingContext2D, chartArea: ReturnType<typeof getChartArea>) => {
    ctx.save();
    ctx.strokeStyle = config.theme.border;
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.strokeRect(chartArea.x, chartArea.y, chartArea.width, chartArea.height);
    ctx.restore();
  };

  // Calculate data ranges
  const getDataRanges = () => {
    if (data.length === 0) return { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };

    const xValues = data.map(d => typeof d.x === 'number' ? d.x : 0);
    const yValues = data.map(d => d.y);

    return {
      xMin: Math.min(...xValues),
      xMax: Math.max(...xValues),
      yMin: Math.min(0, Math.min(...yValues)),
      yMax: Math.max(...yValues),
    };
  };

  // Convert data coordinates to canvas coordinates
  const dataToCanvas = (dataX: number, dataY: number) => {
    const chartArea = getChartArea();
    const { xMin, xMax, yMin, yMax } = getDataRanges();
    
    const x = chartArea.x + ((dataX - xMin) / (xMax - xMin)) * chartArea.width;
    const y = chartArea.y + chartArea.height - ((dataY - yMin) / (yMax - yMin)) * chartArea.height;
    
    return { x, y };
  };

  // Convert canvas coordinates to data coordinates
  const canvasToData = (canvasX: number, canvasY: number) => {
    const chartArea = getChartArea();
    const { xMin, xMax, yMin, yMax } = getDataRanges();
    
    const dataX = xMin + ((canvasX - chartArea.x) / chartArea.width) * (xMax - xMin);
    const dataY = yMax - ((canvasY - chartArea.y) / chartArea.height) * (yMax - yMin);
    
    return { x: dataX, y: dataY };
  };

  // Find nearest data point to mouse position
  const findNearestDataPoint = (mouseX: number, mouseY: number): { dataPoint: ChartDataPoint; index: number } | null => {
    if (!config.interactions || data.length === 0) return null;

    let nearestIndex = -1;
    let nearestDistance = Infinity;

    data.forEach((dataPoint, index) => {
      const { x, y } = dataToCanvas(
        typeof dataPoint.x === 'number' ? dataPoint.x : index,
        dataPoint.y
      );
      
      const distance = Math.sqrt(Math.pow(x - mouseX, 2) + Math.pow(y - mouseY, 2));
      
      if (distance < nearestDistance && distance < 20) { // 20px threshold
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex >= 0 ? { dataPoint: data[nearestIndex], index: nearestIndex } : null;
  };

  // Handle mouse events
  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!config.interactions) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const nearest = findNearestDataPoint(mouseX, mouseY);
    
    if (nearest) {
      const { dataPoint, index } = nearest;
      setHoveredIndex(index);
      
      if (config.showTooltip) {
        setTooltip({
          x: event.clientX,
          y: event.clientY,
          content: (
            <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg">
              <div>Value: {dataPoint.y}</div>
              {dataPoint.label && <div>Label: {dataPoint.label}</div>}
            </div>
          ),
          visible: true,
        });
      }
      
      onDataPointHover?.(dataPoint, index);
    } else {
      setHoveredIndex(null);
      setTooltip(prev => ({ ...prev, visible: false }));
      onDataPointHover?.(null, -1);
    }
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setTooltip(prev => ({ ...prev, visible: false }));
    onDataPointHover?.(null, -1);
  };

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!config.interactions) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const nearest = findNearestDataPoint(mouseX, mouseY);
    
    if (nearest) {
      onDataPointClick?.(nearest.dataPoint, nearest.index);
    }
  };

  // Animation frame logic
  const animate = useCallback((timestamp: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setupCanvas(canvas, ctx);
    
    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);
    
    const chartArea = getChartArea();
    
    // Draw grid and border
    drawGrid(ctx, chartArea);
    drawBorder(ctx, chartArea);
    
    // Use custom render function if provided, otherwise draw default points
    if (customRender) {
      const dataRanges = getDataRanges();
      customRender(ctx, chartArea, dataRanges, dataToCanvas);
    } else if (data.length > 0) {
      const { xMin, xMax, yMin, yMax } = getDataRanges();
      
      ctx.save();
      ctx.fillStyle = config.theme.primary;
      
      data.forEach((dataPoint, index) => {
        const { x, y } = dataToCanvas(
          typeof dataPoint.x === 'number' ? dataPoint.x : index,
          dataPoint.y
        );
        
        const radius = hoveredIndex === index ? 6 : 4;
        const color = dataPoint.color || config.theme.primary;
        
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fill();
      });
      
      ctx.restore();
    }

    if (config.animate) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [data, dimensions, config, hoveredIndex]);

  // Start animation
  useEffect(() => {
    if (config.animate) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      animate(0);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, config.animate]);

  // Loading state
  if (loading) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg ${className}`}
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Loading chart...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg ${className}`}
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 text-sm font-medium">Chart Error</div>
          <div className="text-red-500 dark:text-red-300 text-xs mt-1">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <motion.canvas
        ref={canvasRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="cursor-pointer"
        style={{ 
          width: dimensions.width, 
          height: dimensions.height,
          maxWidth: '100%',
        }}
      />
      
      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 10,
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default BaseChart;